import { useEffect, useState } from "react";

import { apiRequest } from "./api";
import type { LoanProduct } from "./agent-types";

export type LoanProductBadgeSlug = "primary" | "ready" | "derived" | "preview";

export interface LoanProductBadge {
  slug: LoanProductBadgeSlug;
  label: string;
  tone: "default" | "warning" | "accent";
  tooltip?: string;
}

export interface LoanProductCatalogEntry {
  id: string;
  product: LoanProduct;
  term_months: number;
  label: string;
  short_label: string;
  primary_source: string;
  source_kind: "observed" | "editorial";
  badge: LoanProductBadge;
  confidence_ceiling: "high" | "medium";
  cadence_days: number;
  profile_selectable: boolean;
}

interface CatalogResponse {
  products: LoanProductCatalogEntry[];
}

// Module-level cache + in-flight de-dup. Catalog payload is small and
// identical across users (no auth, no PII) so the first request on the
// page warms the cache; subsequent components render synchronously off
// the cached array. The browser HTTP cache (Cache-Control: public,
// max-age=3600) handles cross-tab and cross-session reuse.
let cached: LoanProductCatalogEntry[] | null = null;
let inflight: Promise<LoanProductCatalogEntry[]> | null = null;

export async function fetchLoanProducts(): Promise<LoanProductCatalogEntry[]> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = apiRequest<CatalogResponse>("/api/loan-products")
    .then((r) => {
      cached = r.products ?? [];
      inflight = null;
      return cached;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

// resetLoanProductCache is a test hook — production code should never
// invalidate the catalog cache mid-session. Exported (not _internal-
// prefixed) so vitest can call it from a separate module.
export function resetLoanProductCache() {
  cached = null;
  inflight = null;
}

export function useLoanProducts(): {
  products: LoanProductCatalogEntry[] | null;
  error: unknown;
} {
  const [products, setProducts] = useState<LoanProductCatalogEntry[] | null>(
    cached,
  );
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    // Cache hit was already mirrored into useState's initial value; no
    // need to setProducts again. The effect only fires the fetch when
    // the cache is cold.
    if (cached) return;
    let cancelled = false;
    fetchLoanProducts()
      .then((entries) => {
        if (!cancelled) setProducts(entries);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { products, error };
}
